/**
 * 招待とログインリンクの管理（自社スタッフ専用）。
 *
 * デモや初回導入では、Supabase 標準のメール送信が 2通/時 に制限されていて
 * 複数人を同時にログインさせられない。ここで発行するリンクを手渡しすれば、
 * メールを経由せずにログインしてもらえる。
 *
 * **認証を迂回しているわけではない。** 発行されるのは Supabase が正規に出す
 * 1回限り・短時間で失効するマジックリンクで、通常のログインと同じ経路を通る。
 * 招待されていないアドレスには発行できない（サーバ側で弾いている）。
 */
import { useEffect, useState } from 'react'
import { supabase, type Profile } from '../lib/supabase.ts'
import { WatermarkPreview } from '../components/WatermarkPreview.tsx'
import { TabbedPanel, type TabDef } from '../components/TabbedPanel.tsx'
import { EmailSettings } from './settings/EmailSettings.tsx'
import { AccountSettings } from './settings/AccountSettings.tsx'
import { CategorySettings } from './settings/CategorySettings.tsx'
import { FieldSettings } from './settings/FieldSettings.tsx'
import { SyncSettings } from './settings/SyncSettings.tsx'
import { can, roleLabel, isStaffRole, STAFF_ROLES, type Role } from '../lib/roles.ts'
import {
  listInvitations,
  createInvitation,
  deleteInvitation,
  issueLoginLink,
  getWatermarkSettings,
  updateWatermarkSettings,
  type WatermarkSettings,
  ApiError,
  type Invitation,
} from '../lib/api.ts'

/**
 * 設定ページ。
 *
 * 設定項目は増え続けるので、縦に積まずタブで分ける。
 * 節を1つ足すときは、この配列に1件加えるだけでよい。
 * タブは権限（roles.ts のマトリクス）で出し分ける。隠すのは操作性のためで、
 * 権限の担保は API と RLS にある。
 */
export function Settings({ profile }: { profile: Profile }) {
  const role = profile.role
  const tabs: TabDef[] = [
    ...(can(role, 'customer.manage')
      ? [
          {
            id: 'invitations',
            label: '招待とログイン',
            render: () => <InvitationSettings profile={profile} />,
          },
          {
            id: 'accounts',
            label: 'アカウント',
            render: () => <AccountSettings profile={profile} />,
          },
          { id: 'sync', label: '同期', render: () => <SyncSettings /> },
        ]
      : []),
    ...(can(role, 'category.manage')
      ? [
          { id: 'categories', label: 'カテゴリ', render: () => <CategorySettings /> },
          { id: 'fields', label: '項目', render: () => <FieldSettings /> },
        ]
      : []),
    ...(can(role, 'settings.watermark')
      ? [{ id: 'watermark', label: '透かし', render: () => <WatermarkManager /> }]
      : []),
    ...(can(role, 'settings.email')
      ? [{ id: 'email', label: 'メール送信', render: () => <EmailSettings /> }]
      : []),
  ]
  return (
    <div className="pad settings">
      <h2>設定</h2>
      <TabbedPanel tabs={tabs} storageKey="settings.tab" />
    </div>
  )
}

type Org = { id: string; name: string }

/** 招待取り消しのエラーコード → 日本語。未知のコードは生値のまま出す。 */
const DELETE_INVITATION_ERROR: Record<string, string> = {
  already_accepted: 'すでに受理された招待は取り消せません（アカウントタブで停止してください）',
  staff_login_link_requires_owner: 'スタッフ宛の操作は最高管理者のみ行えます',
  staff_invite_requires_owner: 'スタッフ宛の操作は最高管理者のみ行えます',
  permission_denied: '権限がありません',
}

function InvitationSettings({ profile }: { profile: Profile }) {
  const [invitations, setInvitations] = useState<Invitation[]>([])
  const [orgs, setOrgs] = useState<Org[]>([])
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<Role>('org_member')
  const [orgId, setOrgId] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [links, setLinks] = useState<Record<string, { url: string; code: string | null }>>({})
  const [copied, setCopied] = useState<string | null>(null)

  // スタッフ招待は最高管理者のみ（サーバ側でも 403 'staff_invite_requires_owner' になる）
  const canInviteStaff = can(profile.role, 'staff.manage')
  const invitingStaff = isStaffRole(role)

  const reload = async () => {
    try {
      const { invitations } = await listInvitations()
      setInvitations(invitations)
    } catch (e) {
      setError(e instanceof ApiError ? e.code : String(e))
    }
  }

  useEffect(() => {
    reload()
    supabase
      .from('organizations')
      .select('id,name')
      .order('name')
      .then(({ data }) => {
        const rows = (data ?? []) as Org[]
        setOrgs(rows)
        if (rows[0]) setOrgId(rows[0].id)
      })
  }, [])

  const invite = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      // 運営スタッフは組織に属さない（org_id は必ず NULL）。orgId を送らない
      await createInvitation({
        email: email.trim(),
        role,
        ...(invitingStaff ? {} : { orgId }),
      })
      setEmail('')
      await reload()
    } catch (e) {
      const code = e instanceof ApiError ? e.code : String(e)
      setError(
        code === 'already_invited'
          ? 'このアドレスは既に招待済みです'
          : code === 'user_already_registered'
            ? // 招待し直してもロールは変わらない（サーバが 409 で断る）。
              // 正しい導線を明示する。
              'このアドレスは既に登録済みです。ロールの変更は「アカウント」タブで行ってください'
            : code === 'staff_invite_requires_owner'
              ? `スタッフの招待は最高管理者のみ行えます（${code}）`
              : `招待に失敗しました（${code}）`,
      )
    } finally {
      setBusy(false)
    }
  }

  /**
   * 招待の取り消し。
   *
   * 受理前（プロフィール未作成）の招待だけが対象。受理後は 409 になるので、
   * その場合はアカウントタブでの停止に誘導する。
   */
  const cancel = async (inv: Invitation) => {
    if (!confirm(`${inv.email} への招待を取り消します。発行済みのログインリンクも使えなくなります。よろしいですか？`)) return
    setBusy(true)
    setError(null)
    try {
      await deleteInvitation(inv.id)
      await reload()
    } catch (e) {
      const code = e instanceof ApiError ? e.code : String(e)
      const label = DELETE_INVITATION_ERROR[code]
      setError(label ? `${label}（${code}）` : `招待の取り消しに失敗しました（${code}）`)
    } finally {
      setBusy(false)
    }
  }

  const issue = async (target: string) => {
    setBusy(true)
    setError(null)
    try {
      const { loginUrl, code } = await issueLoginLink(target)
      setLinks((prev) => ({ ...prev, [target]: { url: loginUrl, code } }))
    } catch (e) {
      setError(`リンクの発行に失敗しました（${e instanceof ApiError ? e.code : String(e)}）`)
    } finally {
      setBusy(false)
    }
  }

  const copy = async (target: string, text: string, what: string) => {
    await navigator.clipboard.writeText(text)
    setCopied(`${target}:${what}`)
    setTimeout(() => setCopied(null), 2000)
  }

  return (
    <div className="admin">
      <p className="muted small">
        招待した相手にログインリンクを発行できます。メール送信を経由しないので、
        Supabase の送信制限（2通/時）を受けません。<strong>1回限り・1時間で失効します。</strong>
        <br />
        <strong>コードを渡してください。</strong>リンクとコードは同じトークンなので、
        両方渡しても保険にはなりません。一方リンクは、チャットのプレビュー機能が本人より先に
        開いてしまうと届いた時点で使用済みになり、SMS では折り返しでURLが壊れます。
        コードならどちらも起きません。
      </p>

      <form onSubmit={invite} className="invite-form">
        <div className="row">
          <div>
            <label htmlFor="inv-email">メールアドレス</label>
            <input
              id="inv-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="collaborator@example.com"
              required
            />
          </div>
          <div>
            <label htmlFor="inv-org">所属組織</label>
            <select
              id="inv-org"
              value={orgId}
              disabled={invitingStaff}
              onChange={(e) => setOrgId(e.target.value)}
            >
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
            {invitingStaff && <p className="muted small">運営スタッフは組織に属しません</p>}
          </div>
          <div>
            <label htmlFor="inv-role">権限</label>
            <select id="inv-role" value={role} onChange={(e) => setRole(e.target.value as Role)}>
              <option value="org_member">{roleLabel('org_member')}（自分の履歴のみ閲覧）</option>
              <option value="org_admin">{roleLabel('org_admin')}（組織の全履歴を閲覧）</option>
              {/* スタッフ招待は最高管理者のみ。選択肢ごと出さない（選ばせてから 403 にしない） */}
              {canInviteStaff &&
                STAFF_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {roleLabel(r)}（運営スタッフ）
                  </option>
                ))}
            </select>
          </div>
        </div>
        <button className="primary" disabled={busy || !email || (!invitingStaff && !orgId)}>
          {busy ? '処理中…' : '招待する'}
        </button>
      </form>

      {error && <p className="error small">{error}</p>}

      <table className="invites">
        <thead>
          <tr>
            <th>メールアドレス</th>
            <th>組織 / 権限</th>
            <th>状態</th>
            <th>ログインリンク</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {invitations.map((inv) => (
            <tr key={inv.id}>
              <td className="mono small">{inv.email}</td>
              <td className="small">
                {inv.org_name ?? '運営スタッフ'}
                <span className="muted"> / {roleLabel(inv.role)}</span>
              </td>
              <td className="small">
                {/* accepted_at はリンク発行でも埋まるので、ここでは使わない。
                    実際にログインが成立したか（last_sign_in_at）で判定する。 */}
                {!inv.last_sign_in_at ? (
                  <span className="muted">{inv.has_auth_account ? '未ログイン（発行済み）' : '未ログイン'}</span>
                ) : inv.has_profile ? (
                  <span className="success">ログイン済み</span>
                ) : (
                  <span className="error">要対応：権限なし</span>
                )}
              </td>
              <td>
                {links[inv.email] ? (
                  <div className="issued">
                    {links[inv.email]!.code && (
                      <div className="otp">
                        <code>{links[inv.email]!.code}</code>
                        <button
                          className="link"
                          onClick={() => copy(inv.email, links[inv.email]!.code!, 'code')}
                        >
                          {copied === `${inv.email}:code` ? 'コピーしました' : 'コードをコピー'}
                        </button>
                      </div>
                    )}
                    {/* リンクは畳んでおく。既定の受け渡し手段はコード。 */}
                    <details>
                      <summary className="muted small">リンクが必要な場合</summary>
                      <button
                        className="link"
                        onClick={() => copy(inv.email, links[inv.email]!.url, 'url')}
                      >
                        {copied === `${inv.email}:url` ? 'コピーしました' : 'リンクをコピー'}
                      </button>
                    </details>
                  </div>
                ) : (
                  <button
                    className="small"
                    disabled={busy}
                    onClick={() => {
                      // 再発行すると先に渡した分が失効するので、取り違えを防ぐ
                      if (inv.has_auth_account && !confirm(`${inv.email} のコードを発行し直します。\n先に渡したコード・リンクは使えなくなります。よろしいですか？`)) return
                      issue(inv.email)
                    }}
                  >
                    発行する
                  </button>
                )}
              </td>
              <td>
                {/* 取り消せるのは受理前（プロフィール未作成）の招待だけ。
                    受理後の無効化はアカウントタブの「停止」で行う。 */}
                {!inv.has_profile && (
                  <button className="small danger" disabled={busy} onClick={() => cancel(inv)}>
                    取り消す
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {invitations.length === 0 && <p className="muted small">まだ招待がありません。</p>}
    </div>
  )
}

const POSITIONS: { value: string; label: string }[] = [
  { value: 'top-left', label: '左上' },
  { value: 'top-center', label: '上中央' },
  { value: 'top-right', label: '右上' },
  { value: 'middle-left', label: '左中央' },
  { value: 'middle-center', label: '中央' },
  { value: 'middle-right', label: '右中央' },
  { value: 'bottom-left', label: '左下' },
  { value: 'bottom-center', label: '下中央' },
  { value: 'bottom-right', label: '右下' },
]

/**
 * 透かしの設定。
 *
 * ここで決めた見た目が、動画のプロキシと写真のサムネイル・プレビューに
 * 共通で焼き込まれる。**既存の派生物は作り直されない** — 設定変更後に
 * 生成したものから新しい見た目になる。全件を自動で作り直すと、
 * 数十分〜数時間の変換が黙って走り出すのでそうしていない。
 */
function WatermarkManager() {
  const [settings, setSettings] = useState<WatermarkSettings | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    getWatermarkSettings()
      .then((r) => setSettings(r.settings))
      .catch((e) => setError(e instanceof ApiError ? e.code : String(e)))
  }, [])

  if (!settings) {
    return (
      <section className="watermark">
        {error ? <p className="error small">読み込めませんでした（{error}）</p> : <p className="muted small">読み込み中…</p>}
      </section>
    )
  }

  // numeric は API から文字列で返ることがあるので、必ず数値に寄せる
  const n = (v: number | string) => Number(v)
  const patch = (next: Partial<WatermarkSettings>) =>
    setSettings((prev) => (prev ? { ...prev, ...next } : prev))

  const save = async () => {
    setBusy(true)
    setError(null)
    setSaved(false)
    try {
      const r = await updateWatermarkSettings({
        label: settings.label,
        position: settings.position,
        fontScale: n(settings.font_scale),
        opacity: n(settings.opacity),
        boxEnabled: settings.box_enabled,
        boxOpacity: n(settings.box_opacity),
        timecodeEnabled: settings.timecode_enabled,
        applyToImages: settings.apply_to_images,
      })
      if (r.settings) setSettings(r.settings)
      setSaved(true)
    } catch (e) {
      setError(`保存できませんでした（${e instanceof ApiError ? e.code : String(e)}）`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="watermark">
      <p className="muted small">
        動画のプロキシと、写真のサムネイル・プレビューに共通で焼き込まれます。
        <strong>すでに生成済みの素材は作り直されません。</strong>
        変更を過去の素材にも反映するには、カタログの詳細画面から個別に再生成してください。
      </p>

      <div className="wm-layout">
        <div className="wm-fields">
          <label htmlFor="wm-label">文字列（日本語可・120文字まで）</label>
          <input
            id="wm-label"
            value={settings.label}
            maxLength={120}
            onChange={(e) => patch({ label: e.target.value })}
            placeholder="株式会社サンプル 無断転載禁止"
          />

          <label htmlFor="wm-position">位置</label>
          <select
            id="wm-position"
            value={settings.position}
            onChange={(e) => patch({ position: e.target.value as WatermarkSettings['position'] })}
          >
            {POSITIONS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>

          <label htmlFor="wm-size">
            文字サイズ（画像の高さに対する比率）: {(n(settings.font_scale) * 100).toFixed(1)}%
          </label>
          <input
            id="wm-size"
            type="range"
            min={0.01}
            max={0.15}
            step={0.005}
            value={n(settings.font_scale)}
            onChange={(e) => patch({ font_scale: Number(e.target.value) })}
          />
          <p className="muted small">
            比率で持つので、サムネイル・プロキシ・プレビューのどれでも同じ見え方になります。
          </p>

          <label htmlFor="wm-opacity">文字の不透明度: {(n(settings.opacity) * 100).toFixed(0)}%</label>
          <input
            id="wm-opacity"
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={n(settings.opacity)}
            onChange={(e) => patch({ opacity: Number(e.target.value) })}
          />

          <label className="checkbox">
            <input
              type="checkbox"
              checked={settings.box_enabled}
              onChange={(e) => patch({ box_enabled: e.target.checked })}
            />
            下敷きの黒帯を敷く（明るい映像でも読めるようにする）
          </label>

          {settings.box_enabled && (
            <>
              <label htmlFor="wm-box">黒帯の濃さ: {(n(settings.box_opacity) * 100).toFixed(0)}%</label>
              <input
                id="wm-box"
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={n(settings.box_opacity)}
                onChange={(e) => patch({ box_opacity: Number(e.target.value) })}
              />
            </>
          )}

          <label className="checkbox">
            <input
              type="checkbox"
              checked={settings.apply_to_images}
              onChange={(e) => patch({ apply_to_images: e.target.checked })}
            />
            写真（サムネイル・プレビュー）にも入れる
          </label>

          <label className="checkbox">
            <input
              type="checkbox"
              checked={settings.timecode_enabled}
              onChange={(e) => patch({ timecode_enabled: e.target.checked })}
            />
            動画にタイムコードを焼く（流出時にどの時点かを特定できる）
          </label>

          <button className="primary" disabled={busy} onClick={save}>
            {busy ? '保存中…' : '保存する'}
          </button>
          {saved && <span className="success small">保存しました</span>}
          {error && <p className="error small">{error}</p>}
        </div>

        <WatermarkPreview
          label={settings.label}
          position={settings.position}
          fontScale={n(settings.font_scale)}
          opacity={n(settings.opacity)}
          boxEnabled={settings.box_enabled}
          boxOpacity={n(settings.box_opacity)}
        />
      </div>
    </section>
  )
}
